import React, { ReactNode } from 'react';
import { Stack } from '../Stack';
import { theme } from '../../theme';

import {
  Wrapper,
  BorderedWrapper,
  RootLayout,
  InputLayout,
  AccessoryLayout,
  TextLayout,
  HintText,
  ErrorText,
  BlockTooltip,
} from './components/styled';
import { HelpIcon } from '../../tempIcons/HelpIcon';
import { Text } from '../Text';
import { TextVariant } from '../Text/types';

export interface ForwardedChoiceProps {
  className?: string;
  disabled?: boolean;
  label?: string;
  hint?: string;
  tooltip?: string;
  error?: string;
  bordered?: boolean;
  Badge?: ReactNode;
  Accessory?: ReactNode;
  Footer?: ReactNode;
  inputPosition?: 'left' | 'right';
  labelVariant?: TextVariant;
}

export interface ChoiceProps extends ForwardedChoiceProps {
  id: string;
  children: ReactNode;
}

const generateClassNames = (prefix?: string): any => ({
  hint: prefix ? 'choice__hint' : undefined,
  label: prefix ? 'choice__label' : undefined,
  tooltip: prefix ? 'choice__tooltip' : undefined,
  error: prefix ? 'choice__error' : undefined,
});

export const Choice = ({
  id,
  className,
  label,
  hint,
  tooltip,
  error,
  bordered,
  Badge,
  Accessory,
  Footer,
  children,
  disabled = false,
  labelVariant = 'body',
  inputPosition = 'left',
}: ChoiceProps) => {
  const classNames = generateClassNames(className);

  const contentMarkup = (
    <RootLayout>
      {inputPosition === 'left' && (
        <InputLayout align={Accessory ? 'center' : 'top'}>{children}</InputLayout>
      )}
      {Accessory && <AccessoryLayout>{Accessory}</AccessoryLayout>}
      {label || hint || error || Badge || Footer ? (
        <TextLayout>
          <Stack direction="horizontal" alignY="center" spacing="sm">
            {Badge}
            {label && (
              <Text variant={labelVariant} className={classNames.label}>
                {label}
              </Text>
            )}
            {tooltip && (
              <BlockTooltip text={tooltip} className={classNames.tooltip}>
                <HelpIcon
                  name="help"
                  width={theme.sizes.base}
                  color={theme.colors.neutral.ink.lightest}
                />
              </BlockTooltip>
            )}
          </Stack>
          {hint && <HintText className={classNames.hint}>{hint}</HintText>}
          {error && <ErrorText className={classNames.error}>{error}</ErrorText>}
          {Footer}
        </TextLayout>
      ) : null}
      {inputPosition === 'right' && (
        <InputLayout align={Accessory ? 'center' : 'top'}>{children}</InputLayout>
      )}
    </RootLayout>
  );

  if (bordered) {
    return (
      <BorderedWrapper disabled={disabled} htmlFor={id} className={className}>
        {contentMarkup}
      </BorderedWrapper>
    );
  }

  return (
    <Wrapper disabled={disabled} htmlFor={id} className={className}>
      {contentMarkup}
    </Wrapper>
  );
};
